import { useState, useEffect } from 'react';

interface QuizQuestion {
  id: string;
  question_text: string;
  options: string[];
  correct_index: number;
  order_index: number;
  option_explanations: string[];
}

interface EvaluacionSectionProps {
  questions: QuizQuestion[];
  onQuizPassed?: () => void;
}

type QuizState = 'answering' | 'checked' | 'finished';

export default function EvaluacionSection({ questions, onQuizPassed }: EvaluacionSectionProps) {
  const [current, setCurrent] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [quizState, setQuizState] = useState<QuizState>('answering');
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<{ questionIndex: number; selected: number; correct: number }[]>([]);

  // Notify parent when quiz is passed (≥70%) — must be before any early return
  useEffect(() => {
    if (quizState === 'finished' && questions.length > 0) {
      const pct = Math.round((score / questions.length) * 100);
      if (pct >= 70 && onQuizPassed) {
        onQuizPassed();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizState]);

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-5">
        <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: 'rgba(248,246,243,0.05)', border: '1px solid rgba(248,246,243,0.08)' }}>
          <i className="ri-questionnaire-line text-[#F8F6F3]/25 text-3xl" />
        </div>
        <p className="text-[#F8F6F3]/40 text-sm">El cuestionario de este módulo estará disponible próximamente.</p>
      </div>
    );
  }

  const q = questions[current];
  const progress = quizState === 'finished' ? 100 : ((current + (quizState === 'checked' ? 1 : 0)) / questions.length) * 100;
  const isCorrect = selectedOption === q?.correct_index;

  const handleCheck = () => {
    if (selectedOption === null) return;
    const correct = selectedOption === q.correct_index;
    if (correct) {
      setScore((s) => s + 1);
      setResults((prev) => [...prev, { questionIndex: current, selected: selectedOption, correct: q.correct_index }]);
    }
    setQuizState('checked');
  };

  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent((c) => c + 1);
      setSelectedOption(null);
      setQuizState('answering');
    } else {
      setQuizState('finished');
    }
  };

  const handleRetry = () => {
    setSelectedOption(null);
    setQuizState('answering');
  };

  const handleRestart = () => {
    setCurrent(0);
    setSelectedOption(null);
    setQuizState('answering');
    setScore(0);
    setResults([]);
  };

  // === FINISHED STATE ===
  if (quizState === 'finished') {
    const pct = Math.round((score / questions.length) * 100);
    const passed = pct >= 70;

    return (
      <div className="max-w-2xl mx-auto py-6">
        {/* Score hero */}
        <div className="text-center mb-10">
          <div
            className="w-24 h-24 flex items-center justify-center rounded-full mx-auto mb-5"
            style={{
              background: passed ? 'rgba(139,157,131,0.12)' : 'rgba(201,123,99,0.12)',
              border: `2px solid ${passed ? 'rgba(139,157,131,0.40)' : 'rgba(201,123,99,0.35)'}`,
            }}
          >
            <i
              className={`${passed ? 'ri-trophy-line' : 'ri-refresh-line'} text-4xl`}
              style={{ color: passed ? '#8B9D83' : '#C97B63' }}
            />
          </div>
          <p className="text-[10px] uppercase tracking-[4px] mb-2" style={{ color: passed ? '#8B9D83' : '#C97B63' }}>
            {passed ? 'Evaluación superada' : 'Sigue practicando'}
          </p>
          <h2
            className="text-[#F8F6F3] font-bold mb-3"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', letterSpacing: '-0.5px' }}
          >
            {score} / {questions.length}
          </h2>
          <p className="text-[#F8F6F3]/50 text-sm">
            {passed
              ? `Has acertado el ${pct}% de las preguntas. ¡Excelente comprensión del material!`
              : `Has acertado el ${pct}%. Repasa el video y vuelve a intentarlo.`}
          </p>
        </div>

        {/* Result summary per question */}
        <div className="space-y-4 mb-8">
          {results.map((r, idx) => {
            const qData = questions[r.questionIndex];
            const wasCorrect = r.selected === r.correct;
            return (
              <div
                key={idx}
                className="rounded-2xl p-5"
                style={{
                  background: wasCorrect ? 'rgba(139,157,131,0.07)' : 'rgba(201,123,99,0.07)',
                  border: `1px solid ${wasCorrect ? 'rgba(139,157,131,0.20)' : 'rgba(201,123,99,0.18)'}`,
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                    style={{ background: wasCorrect ? 'rgba(139,157,131,0.20)' : 'rgba(201,123,99,0.18)' }}
                  >
                    <i
                      className={wasCorrect ? 'ri-check-line' : 'ri-close-line'}
                      style={{ color: wasCorrect ? '#8B9D83' : '#C97B63', fontSize: '13px' }}
                    />
                  </div>
                  <p className="text-[#F8F6F3]/80 text-sm font-medium leading-snug">{qData.question_text}</p>
                </div>

                {/* Selected option */}
                <div className="ml-10 space-y-2">
                  <div
                    className="flex items-start gap-2 p-3 rounded-xl"
                    style={{
                      background: wasCorrect ? 'rgba(139,157,131,0.10)' : 'rgba(201,123,99,0.10)',
                      border: `1px solid ${wasCorrect ? 'rgba(139,157,131,0.25)' : 'rgba(201,123,99,0.22)'}`,
                    }}
                  >
                    <span
                      className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5"
                      style={{ background: wasCorrect ? '#8B9D83' : '#C97B63', color: '#1A1C20' }}
                    >
                      {['A', 'B', 'C', 'D'][r.selected]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#F8F6F3]/70 text-xs leading-relaxed mb-1">{qData.options[r.selected]}</p>
                      {qData.option_explanations?.[r.selected] && (
                        <p className="text-xs leading-relaxed" style={{ color: wasCorrect ? '#8B9D83' : '#C97B63' }}>
                          {qData.option_explanations[r.selected]}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Correct answer if was wrong */}
                  {!wasCorrect && (
                    <div
                      className="flex items-start gap-2 p-3 rounded-xl"
                      style={{ background: 'rgba(139,157,131,0.08)', border: '1px solid rgba(139,157,131,0.20)' }}
                    >
                      <span className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5" style={{ background: '#8B9D83', color: '#1A1C20' }}>
                        {['A', 'B', 'C', 'D'][r.correct]}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F8F6F3]/50 text-[10px] uppercase tracking-[1.5px] mb-0.5">Respuesta correcta</p>
                        <p className="text-[#F8F6F3]/70 text-xs leading-relaxed mb-1">{qData.options[r.correct]}</p>
                        {qData.option_explanations?.[r.correct] && (
                          <p className="text-[#8B9D83] text-xs leading-relaxed">{qData.option_explanations[r.correct]}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleRestart}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-80"
            style={{ background: 'rgba(248,246,243,0.06)', color: 'rgba(248,246,243,0.50)', border: '1px solid rgba(248,246,243,0.10)' }}
          >
            <i className="ri-refresh-line" /> Repetir evaluación
          </button>
        </div>
      </div>
    );
  }

  // === QUESTION STATE ===
  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Evaluación del Módulo</p>
          <p className="text-[#F8F6F3]/40 text-sm">
            Pregunta {current + 1} de {questions.length}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[#F8F6F3]/40 text-[10px] mb-1">Progreso</p>
          <p className="text-[#F8F6F3] font-bold text-lg">{Math.round(progress)}%</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 rounded-full mb-10" style={{ background: 'rgba(248,246,243,0.08)' }}>
        <div
          className="h-1 rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #8B9D83, #C97B63)' }}
        />
      </div>

      {/* Question Card */}
      <div className="rounded-2xl p-8 mb-5" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}>
        <div className="flex items-center gap-2 mb-6">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ background: 'rgba(139,157,131,0.12)', border: '1px solid rgba(139,157,131,0.20)' }}
          >
            <i className="ri-questionnaire-line text-[#8B9D83] text-xs" />
          </div>
          <p className="text-[#F8F6F3]/30 text-[10px] uppercase tracking-[2px]">Pregunta {current + 1}</p>
        </div>

        <h3
          className="text-[#F8F6F3] font-medium mb-8 leading-relaxed"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem' }}
        >
          {q.question_text}
        </h3>

        <div className="space-y-3">
          {q.options.map((opt, i) => {
            let bg = 'rgba(248,246,243,0.03)';
            let border = 'rgba(248,246,243,0.06)';
            let labelBg = 'rgba(248,246,243,0.08)';
            let labelColor = 'rgba(248,246,243,0.4)';
            let textColor = 'rgba(248,246,243,0.60)';

            if (quizState === 'checked') {
              if (i === selectedOption && isCorrect) {
                // Respuesta correcta seleccionada → verde
                bg = 'rgba(139,157,131,0.10)';
                border = 'rgba(139,157,131,0.35)';
                labelBg = '#8B9D83';
                labelColor = '#1A1C20';
                textColor = 'rgba(248,246,243,0.85)';
              } else if (i === selectedOption && !isCorrect) {
                // Respuesta incorrecta seleccionada → terracota
                bg = 'rgba(201,123,99,0.08)';
                border = 'rgba(201,123,99,0.30)';
                labelBg = '#C97B63';
                labelColor = '#1A1C20';
                textColor = 'rgba(248,246,243,0.70)';
              } else {
                // Resto de opciones → atenuadas (sin revelar la correcta si falló)
                labelColor = 'rgba(248,246,243,0.20)';
                textColor = 'rgba(248,246,243,0.25)';
              }
            } else if (i === selectedOption) {
              bg = 'rgba(139,157,131,0.10)';
              border = 'rgba(139,157,131,0.40)';
              labelBg = '#8B9D83';
              labelColor = '#1A1C20';
              textColor = 'rgba(248,246,243,1)';
            }

            return (
              <button
                key={i}
                onClick={() => quizState === 'answering' && setSelectedOption(i)}
                disabled={quizState === 'checked'}
                className="w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer disabled:cursor-default"
                style={{ background: bg, border: `1px solid ${border}` }}
              >
                <span
                  className="w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5 transition-all duration-200"
                  style={{ background: labelBg, color: labelColor }}
                >
                  {quizState === 'checked' && i === selectedOption && isCorrect ? (
                    <i className="ri-check-line text-xs" />
                  ) : quizState === 'checked' && i === selectedOption && !isCorrect ? (
                    <i className="ri-close-line text-xs" />
                  ) : (
                    ['A', 'B', 'C', 'D'][i]
                  )}
                </span>
                <span className="text-sm leading-relaxed transition-all duration-200" style={{ color: textColor }}>
                  {opt}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Explanation panel — shown after checking */}
      {quizState === 'checked' && (
        isCorrect ? (
          // ✅ CORRECTO: muestra todas las explicaciones
          <div
            className="rounded-2xl p-5 mb-5 transition-all duration-300"
            style={{ background: 'rgba(139,157,131,0.08)', border: '1px solid rgba(139,157,131,0.25)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(139,157,131,0.20)' }}>
                <i className="ri-check-line" style={{ color: '#8B9D83', fontSize: '14px' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-[2px]" style={{ color: '#8B9D83' }}>
                ¡Correcto!
              </span>
            </div>

            {/* Explicación de la opción correcta */}
            {selectedOption !== null && q.option_explanations?.[selectedOption] && (
              <div className="mb-4 pb-4" style={{ borderBottom: '1px solid rgba(139,157,131,0.15)' }}>
                <p className="text-[#8B9D83] text-[10px] uppercase tracking-[2px] mb-1.5">Por qué es correcta</p>
                <p className="text-[#8B9D83]/85 text-sm leading-relaxed">{q.option_explanations[selectedOption]}</p>
              </div>
            )}

            {/* Explicaciones de las opciones incorrectas */}
            {q.options.some((_, i) => i !== q.correct_index && q.option_explanations?.[i]) && (
              <div className="space-y-3">
                <p className="text-[#F8F6F3]/30 text-[10px] uppercase tracking-[2px]">Por qué las demás no son correctas</p>
                {q.options.map((opt, i) => {
                  if (i === q.correct_index) return null;
                  if (!q.option_explanations?.[i]) return null;
                  return (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 mt-0.5" style={{ background: 'rgba(201,123,99,0.18)', color: '#C97B63' }}>
                        {['A', 'B', 'C', 'D'][i]}
                      </span>
                      <p className="text-[#F8F6F3]/45 text-xs leading-relaxed">{q.option_explanations[i]}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          // ❌ INCORRECTO: solo muestra por qué la seleccionada es incorrecta
          <div
            className="rounded-2xl p-5 mb-5 transition-all duration-300"
            style={{ background: 'rgba(201,123,99,0.08)', border: '1px solid rgba(201,123,99,0.22)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(201,123,99,0.18)' }}>
                <i className="ri-close-line" style={{ color: '#C97B63', fontSize: '14px' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-[2px]" style={{ color: '#C97B63' }}>
                Respuesta incorrecta
              </span>
            </div>

            {selectedOption !== null && q.option_explanations?.[selectedOption] && (
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(201,123,99,0.85)' }}>
                {q.option_explanations[selectedOption]}
              </p>
            )}
          </div>
        )
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div />
        {quizState === 'answering' ? (
          <button
            onClick={handleCheck}
            disabled={selectedOption === null}
            className="flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            style={{
              background: selectedOption !== null ? '#8B9D83' : 'rgba(248,246,243,0.06)',
              color: selectedOption !== null ? '#1A1C20' : 'rgba(248,246,243,0.3)',
            }}
          >
            Comprobar respuesta <i className="ri-check-line" />
          </button>
        ) : isCorrect ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90"
            style={{ background: '#8B9D83', color: '#1A1C20' }}
          >
            {current < questions.length - 1 ? 'Siguiente pregunta' : 'Ver resultados'}
            <i className="ri-arrow-right-line" />
          </button>
        ) : (
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-90"
            style={{ background: 'rgba(201,123,99,0.15)', color: '#C97B63', border: '1px solid rgba(201,123,99,0.35)' }}
          >
            <i className="ri-refresh-line" /> Volver a intentar
          </button>
        )}
      </div>
    </div>
  );
}
